// src/pages/Stati.jsx - Fixed with direct Supabase calls
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from '@/lib/supabase';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from "framer-motion";
import { Plus, Search } from "lucide-react";
import { useState } from "react";

import StatoForm from "../components/stati/StatoForm";
import StatoTable from "../components/stati/StatoTable";

export default function Stati() {
  const [showForm, setShowForm] = useState(false);
  const [editingStato, setEditingStato] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const queryClient = useQueryClient();

  // Load stati data
  const { data: stati = [], isLoading } = useQuery({
    queryKey: ['stati'],
    queryFn: async () => {
      console.log('Loading stati...');
      const { data, error } = await supabase
        .from('stati')
        .select('*')
        .order('nome');
      if (error) {
        console.error('Stati loading error:', error);
        throw error;
      }
      console.log('Stati loaded:', data.length);
      return data || [];
    }
  });

  // Create mutation
  const createStatoMutation = useMutation({
    mutationFn: async (statoData) => {
      console.log('Creating stato:', statoData);
      const { data, error } = await supabase
        .from('stati')
        .insert(statoData)
        .select()
        .single();
      if (error) {
        console.error('Create stato error:', error);
        throw error;
      }
      console.log('Stato created:', data);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stati'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-data'] });
    }
  });

  // Update mutation
  const updateStatoMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      console.log('Updating stato:', { id, data });
      const { data: result, error } = await supabase
        .from('stati')
        .update(data)
        .eq('id', id)
        .select()
        .single();
      if (error) {
        console.error('Update stato error:', error);
        throw error;
      }
      console.log('Stato updated:', result);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stati'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-data'] });
    }
  });

  // Delete mutation
  const deleteStatoMutation = useMutation({
    mutationFn: async (id) => {
      console.log('Deleting stato:', id);
      const { error } = await supabase
        .from('stati')
        .delete()
        .eq('id', id);
      if (error) {
        console.error('Delete stato error:', error);
        throw error;
      }
      console.log('Stato deleted');
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stati'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-data'] });
    }
  });

  // Filter stati based on search term
  const filteredStati = stati.filter(stato => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      stato.nome?.toLowerCase().includes(searchLower) ||
      stato.descrizione?.toLowerCase().includes(searchLower)
    );
  });

  const handleSubmit = async (statoData) => {
    try {
      if (editingStato) {
        await updateStatoMutation.mutateAsync({ id: editingStato.id, data: statoData });
      } else {
        await createStatoMutation.mutateAsync(statoData);
      }
      setShowForm(false);
      setEditingStato(null);
    } catch (error) {
      console.error("Errore nel salvataggio:", error);
      alert(`Errore nel salvataggio: ${error.message}`);
    }
  };

  const handleEdit = (stato) => {
    setEditingStato(stato);
    setShowForm(true);
  };

  const handleDelete = async (stato) => {
    if (window.confirm("Sei sicuro di voler eliminare questo stato?")) {
      try {
        await deleteStatoMutation.mutateAsync(stato.id);
      } catch (error) {
        console.error("Errore nell'eliminazione:", error);
        alert(`Errore nell'eliminazione: ${error.message}. Il stato potrebbe essere utilizzato da alcune auto.`);
      }
    }
  };

  const isAnyMutationPending = 
    createStatoMutation.isPending || 
    updateStatoMutation.isPending || 
    deleteStatoMutation.isPending;

  return (
    <div className="p-4 md:p-8 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-800 mb-2">Gestione Stati</h1>
            <p className="text-slate-600">Gestisci gli stati delle auto</p>
            {/* Debug info */}
            {/* 
            <div className="text-xs text-gray-500 mt-2">
              Debug: Stati={stati.length}, Loading={isLoading.toString()}
            </div>
            */}
          </div>
          <Button
            onClick={() => setShowForm(!showForm)}
            className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white shadow-lg"
            disabled={isAnyMutationPending}
          >
            <Plus className="w-5 h-5 mr-2" />
            {isAnyMutationPending ? 'Salvando...' : 'Nuovo Stato'}
          </Button>
        </div>

        <AnimatePresence>
          {showForm && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="mb-8"
            >
              <StatoForm
                stato={editingStato}
                onSubmit={handleSubmit}
                onCancel={() => {
                  setShowForm(false);
                  setEditingStato(null);
                }}
                isLoading={isAnyMutationPending}
              />
            </motion.div>
          )}
        </AnimatePresence>

        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Cerca stati..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 glass-effect border-0 shadow-lg"
            />
          </div>
        </div>

        <StatoTable
          stati={filteredStati}
          onEdit={handleEdit}
          onDelete={handleDelete}
          isLoading={isLoading}
          deleteLoading={deleteStatoMutation.isPending}
        />
      </div>
    </div>
  );
}